import { attr } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

export interface TreatmentSectionWithColor {
  key: string;
  label: string;
  color?: string;
  answers: string[];
}

export default abstract class BaseSurgerySurveyFilledJournalEvent extends JournalEvent {
  @attr("number")
  declare score: number;

  @attr("string")
  declare originTaskIdentifier: string;

  static baseValidations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.survey-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      score: validateNumber({
        allowBlank: true,
        description: intl.t("validations.survey-filled-journal-event.score")
      })
    };
  }

  protected computeSectionsWithColor(
    sections: string[],
    scoreValues: Record<string, Record<string, number>>
  ): TreatmentSectionWithColor[] {
    const computeAnswers = (key: string) => {
      return [this.intl.t(`ctcae-custom.${key}.answers.${this[key]}`)];
    };

    const computeColor = (value: number) => {
      return value >= 100 ? "red" : value >= 10 ? "orange" : "green";
    };

    const computeLabel = (key: string) => {
      return this.intl.t(`ctcae-custom.${key}.short`);
    };

    return [
      ...sections.map(key => ({
        key,
        label: computeLabel(key),
        color: computeColor(scoreValues[key][this[key] as string | number]),
        answers: computeAnswers(key)
      })),
      ...(this.score
        ? [
            {
              key: "score",
              label: "Score",
              color: computeColor(this.score),
              answers: [this.score.toString()]
            }
          ]
        : [])
    ];
  }
}
